import { useContext, useEffect, useState } from "react";

import olMap from "ol/Map";
import olLayer from "ol/layer/Layer";
import olVectorTileLayer from "ol/layer/VectorTile";
import olVectorTileSource from "ol/source/VectorTile";
import { StyleLike as olStyleLike } from "ol/style/Style";
import MapContext from "../map/MapContext";

export interface VectorTileProps {
  source: olVectorTileSource;
  style?: olStyleLike;
  visible: boolean;
  zIndex: number;
}

/**
 * Component to register a new OL VectorTile Layer on the Map
 * using the MapContext.
 * @component
 * @param {import("ol/layer/VectorTile").default} source The OL source to use
 * @param {import("ol/style/Style").StyleLike} style The OL style like object to style un-selected features
 * @param {import("ol/style/Style").default|import("ol/style/Style").StyleFunction} selectedStyle The OL style or style function to
 *  to use for selected features
 * @param {SelectEvent} onSelect Callback function fired when a feature is selected.
 * @example
 * <VectorTile
 *   source={vectorTileSource}
 *   style={style} />
 * @remarks
 * This will create 2 VectorTiles layers; one for the unselected features and another for the selected ones.
 * It will also register a click handler with the Map and perform a radius search on the clicked pixel for
 * features within range.
 */
const VectorTile = ({
  source,
  style,
  visible = true,
  zIndex,
}: VectorTileProps) => {
  const map = useContext(MapContext) as olMap;
  const [layer, setLayer] = useState<olLayer | null>(null);

  useEffect(() => {
    if (!map) return () => {};

    const layer = new olVectorTileLayer({
      source,
      style,
      zIndex: zIndex,
    });

    layer.setVisible(visible);
    map.addLayer(layer);
    setLayer(layer);
    return () => {
      if (!map) return;
      map.removeLayer(layer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, source, style]);

  useEffect(() => {
    if (!layer) return;
    layer.setVisible(visible);
  }, [visible]);

  useEffect(() => {
    if (!layer || !zIndex) return;
    layer.setZIndex(zIndex);
  }, [zIndex]);

  return null;
};

export default VectorTile;
